import axios from 'axios'
import { httpClient } from '../httpClient'
import {
  newPlaceholderNode,
  NodeType,
  ParentIds,
  TreeNode
} from './treeModel'

export interface QueryNodesResult {
  success: boolean
  status: number
  data?: TreeNode[]
  error?: string
}

export const queryNodes = async (
  parentIds: ParentIds = {}
): Promise<QueryNodesResult> => {
  let url: string
  if (parentIds.itemId) {
    url = getItemsUrl(parentIds.driveId!, parentIds.itemId)
  } else if (parentIds.driveId) {
    url = getItemsUrl(parentIds.driveId, null)
  } else if (parentIds.siteId) {
    url = getDrivesUrl(parentIds.siteId!)
  } else {
    url = getSitesUrl()
  }

  let response
  try {
    response = await httpClient.get(url, {
      validateStatus: status => [200, 401, 403, 404].includes(status)
    })
  } catch (exc) {
    const error = axios.isAxiosError(exc) ? exc.message : "Unknown error"
    const status = axios.isAxiosError(exc) ? exc.response?.status || 0 : 0
    return {
      success: false,
      status,
      error,
    }
  }

  const errorCodeMessages: Record<number, string> = {
    401: "Unauthorized",
    403: "Forbidden",
    404: "Resource not found",
  }

  if (response.status in errorCodeMessages) {
    return {
      success: false,
      status: response.status,
      error: response.data.message || errorCodeMessages[response.status],
    }
  }

  // Fallback (if some other status slips through)
  if (response.status !== 200) {
    return {
      success: false,
      status: response.status,
      error: "Unexpected status",
    }
  }

  const nodesRaw: {value: any[]} = response.data
  if (!nodesRaw || !nodesRaw.value) {
    return {
      success: true,
      status: response.status,
      data: [],
    }
  }

  const nodes = nodesRaw.value.map(node => {
    const treeNode: TreeNode = {
      id: node.id as string,
      label: node.label as string,
      type: node.type as NodeType,
      webUrl: node.webUrl as string,
      parentIds
    } as TreeNode
    switch (treeNode.type) {
      case 'site':
      case 'drive':
      case 'folder':
        treeNode.hasChildren = node.hasChildren
        treeNode.children = (
          treeNode.hasChildren
          ? [newPlaceholderNode()]
          : []
        )
        break
      default:
        treeNode.hasChildren = false
    }
    return treeNode
  })

  return {
    success: true,
    status: response.status,
    data: nodes,
  }
}

const getSitesUrl = (): string => `/b2cauth/sharepoint/sites/tenant/`

const getDrivesUrl = (siteId: string): string => (
  `/b2cauth/sharepoint/drives/${siteId}/`
)

const getItemsUrl = (driveId: string, itemId: string | null): string => (
  itemId
  ? `/b2cauth/sharepoint/items/${driveId}/${itemId}/`
  : `/b2cauth/sharepoint/items/${driveId}/`
)
